import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
} from '@mui/material';
import {
  Cloud,
  Storage,
  NetworkCheck,
  Logout,
  Person,
} from '@mui/icons-material';
import { cloudsAPI } from '../api';

const Dashboard = ({ onSelectCloud, onLogout }) => {
  const [clouds, setClouds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }

    const fetchClouds = async () => {
      try {
        const response = await cloudsAPI.getClouds();
        setClouds(response.data);
      } catch (err) {
        setError('Failed to load cloud providers');
      } finally {
        setLoading(false);
      }
    };

    fetchClouds();
  }, []);

  const getCloudIcon = (cloudId) => {
    switch (cloudId) {
      case 'aws':
        return <Cloud sx={{ fontSize: 40, color: '#FF9900' }} />;
      case 'gcp':
        return <Cloud sx={{ fontSize: 40, color: '#4285F4' }} />;
      case 'azure':
        return <Cloud sx={{ fontSize: 40, color: '#00A1F1' }} />;
      default:
        return <Cloud sx={{ fontSize: 40 }} />;
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography>Loading cloud providers...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Multi-Cloud Admin Dashboard
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person />
              <Typography variant="body2">
                {user?.username || 'Admin'}
              </Typography>
            </Box>
            <IconButton color="inherit" onClick={onLogout}>
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Cloud Portfolio
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Select a cloud provider to view its resources and manage inventory
        </Typography>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Grid container spacing={3}>
          {clouds.map((cloud) => (
            <Grid item xs={12} sm={6} md={4} key={cloud.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: cloud.status === 'active' ? 'pointer' : 'not-allowed',
                  opacity: cloud.status === 'active' ? 1 : 0.6,
                }}
                onClick={() => cloud.status === 'active' && onSelectCloud(cloud.id)}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    {getCloudIcon(cloud.id)}
                  </Box>
                  
                  <Typography gutterBottom variant="h5" component="h2">
                    {cloud.name}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {cloud.description}
                  </Typography>
                  
                  <Chip
                    label={cloud.status}
                    color={getStatusColor(cloud.status)}
                    size="small"
                  />
                </CardContent>
                
                <CardActions sx={{ justifyContent: 'center' }}>
                  <Button
                    size="small"
                    disabled={cloud.status !== 'active'}
                    onClick={() => cloud.status === 'active' && onSelectCloud(cloud.id)}
                  >
                    View Resources
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Dashboard; 